/**
 * The goal builder: what a person is actually delegating.
 *
 * The same component enables a run and edits a live one, because the two are the same
 * decisions and a separate edit form would drift from the builder within a release. What
 * differs is only whether there is a revision to check against.
 *
 * The parts that are easy to get wrong, and are therefore explicit here:
 *
 * - **A numeric target counts the whole project.** Said beside the field, not in a tooltip,
 *   because "stop after 20" meaning "20 anywhere in Cell" is surprising exactly once and
 *   expensive that once.
 * - **The deadline shows both the wall clock and the zone**, and refuses a time daylight
 *   saving skips rather than quietly moving it.
 * - **Capacity thresholds stop supervision, not spending.** In-flight work and other consumers
 *   keep using the account; the interface says so rather than implying a spend cap.
 * - **Only harnesses with a verified adapter are offered**, and the others are listed with
 *   their reason. A silent substitution would mean a person's chosen model is not the one
 *   making decisions on their project.
 */
import { useEffect, useMemo, useState } from 'react';

import type { NightManagerProjectView, NightManagerRunView } from '@nightshift/ui-types';

import { api } from '../../live/api.ts';
import { RouteListEditor, type RouteMachine, type RouteRow } from '../route-list-editor.tsx';
import type { CatalogueModel } from '../../live/api.ts';
import { buildGoalPayload, type GoalDraft } from '../../lib/night-manager.ts';
import { currentTimezone, describeInstantInZone, instantToZonedWallTime } from '@nightshift/ui-local-time';

type SaveState = { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved' } | { kind: 'failed'; error: string };

function draftFrom(project: NightManagerProjectView, run: NightManagerRunView | undefined): GoalDraft {
  const goal = run?.goal;
  const deadline = goal?.stop.deadline ?? null;
  return {
    scopeKind: goal?.scopeKind ?? 'backlog',
    selectedTicketIds: goal?.selected.map((entry) => entry.ticketId) ?? [],
    stopOnSelectionComplete: goal?.stop.selectionComplete ?? false,
    ticketTarget: goal?.stop.ticketTarget === undefined || goal?.stop.ticketTarget === null ? '' : String(goal.stop.ticketTarget),
    deadlineMode: deadline ? deadline.source : 'none',
    establishedDeadlineAt: deadline?.at ?? null,
    establishedDurationMs: deadline?.durationMs ?? null,
    durationHours: deadline?.durationMs ? String(Math.round((deadline.durationMs / 3_600_000) * 10) / 10) : '',
    deadlineLocal: deadline ? instantToZonedWallTime(deadline.at, deadline.timezone) : '',
    timezone: deadline?.timezone ?? currentTimezone(),
    capacity: (goal?.stop.capacity ?? []).map((condition) => ({
      machineId: condition.machineId,
      provider: condition.provider,
      // The account digest is not shown, but it must survive an edit or the condition would
      // silently re-bind to whichever account is signed in now.
      account: condition.account,
      window: condition.window,
      model: condition.model,
      thresholdPercent: String(condition.thresholdPercent),
    })),
    maxActiveTickets: String(goal?.maxActiveTickets ?? project.settings.maxActiveTickets),
    checkIntervalMinutes: String(goal?.checkIntervalMinutes ?? project.settings.checkIntervalMinutes),
    instructions: goal?.instructions ?? project.settings.instructions,
  };
}

export function GoalBuilder({
  project,
  run,
  onEnabled,
}: {
  project: NightManagerProjectView;
  run?: NightManagerRunView;
  onEnabled: () => Promise<void> | void;
}) {
  const editing = run !== undefined;
  const [draft, setDraft] = useState<GoalDraft>(() => draftFrom(project, run));
  const [routes, setRoutes] = useState<RouteRow[]>(
    () => (run?.routes ?? project.settings.routes).map((route) => ({ ...route })) as RouteRow[],
  );
  const [state, setState] = useState<SaveState>({ kind: 'idle' });
  const [machines, setMachines] = useState<RouteMachine[]>([]);
  const [models, setModels] = useState<CatalogueModel[]>([]);

  // The live registry and catalogue, the same two the other route surfaces read. Narrowed to
  // manager-capable harnesses below, so a person cannot pick a model that would then be
  // quietly served by a different one.
  useEffect(() => {
    let cancelled = false;
    void Promise.all([api.machines().catch(() => null), api.modelsCatalogue(project.projectSlug).catch(() => null)]).then(
      ([hosts, catalogue]) => {
        if (cancelled) return;
        setMachines(
          (hosts?.items ?? []).map((item) => ({
            name: item.name,
            installedHarnesses: item.installedHarnesses,
            live: item.live,
            paused: item.paused,
          })),
        );
        setModels(catalogue?.models ?? []);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [project.projectSlug]);

  const now = Date.now();
  const validation = useMemo(() => buildGoalPayload(draft, now), [draft, now]);
  const problems = validation.ok ? [] : validation.problems;

  // Only manager-capable harnesses are selectable. The rest are named below with a reason,
  // rather than being offered and then silently served by a different model.
  const capable = project.capability.capableHarnesses;
  const managerModels = useMemo(
    () => models.filter((model) => capable.includes(model.harness)),
    [models, capable],
  );
  const managerMachines = useMemo(
    () => machines.filter((machine) => machine.installedHarnesses.some((harness) => capable.includes(harness))),
    [machines, capable],
  );

  const set = <K extends keyof GoalDraft>(key: K, value: GoalDraft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const deadlinePreview =
    draft.deadlineMode === 'deadline' && draft.deadlineLocal && validation.ok
      ? describeInstantInZone(
          (validation.goal.stop as { deadline?: { at: number } }).deadline?.at ?? now,
          draft.timezone,
        )
      : null;

  return (
    <form
      aria-label={editing ? 'Edit the night manager goal' : 'Configure a night manager'}
      style={{ display: 'grid', gap: 8, fontSize: 'var(--fs-small)' }}
      onSubmit={async (event) => {
        event.preventDefault();
        if (!validation.ok) return;
        setState({ kind: 'saving' });
        try {
          if (editing && run) {
            await api.editNightManagerRun(run.id, {
              expectedRevision: run.policyRevision,
              goal: validation.goal,
              routes,
            });
          } else {
            await api.enableNightManager(project.projectSlug, {
              goal: validation.goal,
              routes,
              requestId: crypto.randomUUID(),
            });
          }
          setState({ kind: 'saved' });
          await onEnabled();
        } catch (error) {
          setState({ kind: 'failed', error: error instanceof Error ? error.message : String(error) });
        }
      }}
    >
      <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 6 }}>
        <legend className="kicker">Work scope</legend>
        <label>
          <input
            type="radio"
            name="night-manager-scope"
            checked={draft.scopeKind === 'backlog'}
            onChange={() => set('scopeKind', 'backlog')}
          />{' '}
          The {project.projectName} backlog — choose useful work
        </label>
        <label>
          <input
            type="radio"
            name="night-manager-scope"
            checked={draft.scopeKind === 'selection'}
            onChange={() => set('scopeKind', 'selection')}
          />{' '}
          Selected tickets only
        </label>
        {draft.scopeKind === 'selection' ? (
          <label>
            <span className="dim" style={{ fontSize: 'var(--fs-micro)' }}>
              Ticket ids, comma separated. The manager may read the rest of the project but can only change these and their split children.
            </span>
            <input
              className="input"
              aria-label="Selected ticket ids"
              value={draft.selectedTicketIds.join(', ')}
              onChange={(event) =>
                set(
                  'selectedTicketIds',
                  event.target.value
                    .split(',')
                    .map((part) => Number(part.trim()))
                    .filter((id) => Number.isSafeInteger(id) && id > 0),
                )
              }
            />
          </label>
        ) : null}
      </fieldset>

      <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 6 }}>
        <legend className="kicker">Stop when — any of these</legend>
        {draft.scopeKind === 'selection' ? (
          <label>
            <input
              type="checkbox"
              checked={draft.stopOnSelectionComplete}
              onChange={(event) => set('stopOnSelectionComplete', event.target.checked)}
            />{' '}
            Every selected ticket is Done
          </label>
        ) : null}

        <label>
          <span className="dim" style={{ fontSize: 'var(--fs-micro)' }}>
            Tickets completed (leave empty for none). Counts <strong>any</strong> qualifying ticket completed in {project.projectName} during the run, whoever supervised it. Split children count separately.
          </span>
          <input
            className="input"
            aria-label="Stop after this many tickets complete"
            inputMode="numeric"
            value={draft.ticketTarget}
            onChange={(event) => set('ticketTarget', event.target.value)}
          />
        </label>

        <label>
          <span className="dim" style={{ fontSize: 'var(--fs-micro)' }}>Time limit</span>
          <select
            className="input"
            aria-label="Time limit kind"
            value={draft.deadlineMode}
            onChange={(event) => set('deadlineMode', event.target.value as GoalDraft['deadlineMode'])}
          >
            <option value="none">No time limit</option>
            <option value="duration">Run for a duration</option>
            <option value="deadline">Stop at a date and time</option>
          </select>
        </label>
        {draft.deadlineMode === 'duration' ? (
          <label>
            <span className="dim" style={{ fontSize: 'var(--fs-micro)' }}>Hours</span>
            <input
              className="input"
              aria-label="Duration in hours"
              inputMode="decimal"
              value={draft.durationHours}
              onChange={(event) => set('durationHours', event.target.value)}
            />
          </label>
        ) : null}
        {draft.deadlineMode === 'deadline' ? (
          <>
            <label>
              <span className="dim" style={{ fontSize: 'var(--fs-micro)' }}>Local date and time</span>
              <input
                className="input"
                type="datetime-local"
                aria-label="Stop at this date and time"
                value={draft.deadlineLocal}
                onChange={(event) => set('deadlineLocal', event.target.value)}
              />
            </label>
            <label>
              <span className="dim" style={{ fontSize: 'var(--fs-micro)' }}>Timezone</span>
              <input
                className="input"
                aria-label="Timezone"
                value={draft.timezone}
                onChange={(event) => set('timezone', event.target.value)}
              />
            </label>
            {deadlinePreview ? (
              <p className="dim" style={{ margin: 0, fontSize: 'var(--fs-micro)' }}>Stops at {deadlinePreview}.</p>
            ) : null}
          </>
        ) : null}

        <CapacityConditions project={project} draft={draft} onChange={(capacity) => set('capacity', capacity)} />
      </fieldset>

      <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 6 }}>
        <legend className="kicker">Limits</legend>
        <label>
          <span className="dim" style={{ fontSize: 'var(--fs-micro)' }}>
            Maximum active tickets in this project
          </span>
          <input
            className="input"
            aria-label="Maximum active tickets"
            inputMode="numeric"
            value={draft.maxActiveTickets}
            onChange={(event) => set('maxActiveTickets', event.target.value)}
          />
        </label>
        <label>
          <span className="dim" style={{ fontSize: 'var(--fs-micro)' }}>Periodic check (minutes, 1–120)</span>
          <input
            className="input"
            aria-label="Periodic check in minutes"
            inputMode="numeric"
            value={draft.checkIntervalMinutes}
            onChange={(event) => set('checkIntervalMinutes', event.target.value)}
          />
        </label>
      </fieldset>

      <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 6 }}>
        <legend className="kicker">Execution route</legend>
        <RouteListEditor
          rows={routes}
          onChange={setRoutes}
          machines={managerMachines}
          models={managerModels}
          label="Night manager route"
          emptyText="No route. Add one so the manager has somewhere to run."
          footer={
            <>
              Only {capable.join(', ') || 'no harness'} can host a night manager session.{' '}
              {project.capability.unavailable.map((entry) => `${entry.harness}: ${entry.reason}`).join(' ')}
            </>
          }
        />
      </fieldset>

      <label>
        <span className="dim" style={{ fontSize: 'var(--fs-micro)' }}>
          Instructions (optional). These can make the manager stricter; they cannot remove a required check or widen its authority.
        </span>
        <textarea
          className="input"
          aria-label="Instructions"
          rows={3}
          value={draft.instructions}
          onChange={(event) => set('instructions', event.target.value)}
        />
      </label>

      <details>
        <summary className="dim" style={{ fontSize: 'var(--fs-micro)', cursor: 'pointer' }}>
          What the manager may do
        </summary>
        <p className="dim" style={{ margin: '4px 0 0', fontSize: 'var(--fs-micro)' }}>
          Start eligible tickets, answer clarifications, record decisions, clarify briefs and acceptance criteria,
          approve plans, request bounded retries and evidence repair, send work back with a reason, split oversized
          tickets, and approve landing through the existing merge guards. It cannot mark work Done, reset a repair
          budget, approve a visual baseline, weaken a verification contract, release a hold you placed, deploy,
          run a shell, or touch source code directly. Review is throughput-first: it relies on the existing AI review
          and merge gates and intervenes on clear acceptance failures.
        </p>
      </details>

      {problems.length > 0 ? (
        <ul style={{ margin: 0, paddingLeft: 16, fontSize: 'var(--fs-micro)', color: 'var(--danger, #d06b42)' }}>
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      ) : null}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button type="submit" className="btn btn-accent btn-sm" disabled={!validation.ok || state.kind === 'saving' || routes.length === 0}>
          {state.kind === 'saving' ? (editing ? 'Saving…' : 'Enabling…') : editing ? 'Save changes' : 'Enable'}
        </button>
        <span className="dim" style={{ fontSize: 'var(--fs-micro)' }}>
          {state.kind === 'failed' ? state.error : state.kind === 'saved' ? 'Saved.' : 'In-flight work continues when supervision ends.'}
        </span>
      </div>
    </form>
  );
}

/**
 * Capacity stopping conditions.
 *
 * Offers only windows a machine is currently reporting with a usable figure, because a
 * condition needs a fresh sample to bind — offering an unbindable window would be offering an
 * error message. The account digest travels with the choice so the condition stays pinned to
 * the account it was bound against; it is never displayed.
 */
function CapacityConditions({
  project,
  draft,
  onChange,
}: {
  project: NightManagerProjectView;
  draft: GoalDraft;
  onChange: (capacity: GoalDraft['capacity']) => void;
}) {
  const options = project.capacityOptions;
  if (options.length === 0) {
    return (
      <p className="dim" style={{ margin: 0, fontSize: 'var(--fs-micro)' }}>
        No machine is currently reporting account capacity, so no capacity stopping condition can be set.
      </p>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <span className="dim" style={{ fontSize: 'var(--fs-micro)' }}>
        Account capacity. Stopping on one of these ends supervision; it does not cap spending — work already running
        and anything else using the account keep going.
      </span>
      {draft.capacity.map((entry, index) => (
        <div key={`${entry.machineId}-${entry.window}-${index}`} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ flex: 1 }}>
            {options.find((option) => option.machineId === entry.machineId && option.window === entry.window)?.label ??
              `${entry.provider} ${entry.window}`}
          </span>
          <label>
            <span className="visually-hidden">Threshold percent</span>
            <input
              className="input"
              style={{ width: 70 }}
              inputMode="numeric"
              value={entry.thresholdPercent}
              onChange={(event) => {
                const next = [...draft.capacity];
                next[index] = { ...entry, thresholdPercent: event.target.value };
                onChange(next);
              }}
            />
          </label>
          <span className="dim" style={{ fontSize: 'var(--fs-micro)' }}>% remaining</span>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            aria-label={`Remove capacity condition ${index + 1}`}
            onClick={() => onChange(draft.capacity.filter((_, position) => position !== index))}
          >
            Remove
          </button>
        </div>
      ))}
      <label>
        <span className="visually-hidden">Add a capacity condition</span>
        <select
          className="input"
          value=""
          onChange={(event) => {
            const option = options.find((entry) => `${entry.machineId}|${entry.window}|${entry.model ?? ''}` === event.target.value);
            if (!option) return;
            onChange([
              ...draft.capacity,
              {
                machineId: option.machineId,
                provider: option.provider,
                account: option.account,
                window: option.window,
                model: option.model,
                thresholdPercent: '20',
              },
            ]);
          }}
        >
          <option value="">+ Add a capacity condition</option>
          {options.map((option) => (
            <option key={`${option.machineId}|${option.window}|${option.model ?? ''}`} value={`${option.machineId}|${option.window}|${option.model ?? ''}`}>
              {option.label}
              {option.remainingPercent === null ? '' : ` (${option.remainingPercent}% now)`}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
